<template>
  <div class="lab-offline-docx" v-loading="statusLoading">
    <div class="lod-hd">
      <div class="lod-hd-main">
        <h3 class="lod-title">离线 Word 填报</h3>
        <p class="lod-hint">先下载预填表 → 本机填写 → 再上传；已上传可点「预览」查看页图</p>
      </div>
      <button
        type="button"
        class="lod-refresh"
        :disabled="!xmbh || !formNums.length"
        @click="refreshStatus"
      >
        <svg class="lod-ico" viewBox="0 0 16 16" aria-hidden="true">
          <path fill="currentColor" d="M13.65 2.35A7.96 7.96 0 0 0 8 0C3.58 0 0 3.58 0 8s3.58 8 8 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 8 14a6 6 0 1 1 4.24-10.24L10 6h6V0l-2.35 2.35z"/>
        </svg>
        刷新
      </button>
    </div>

    <div v-if="!offlineRows.length" class="lod-empty">请先在上方勾选检测原表</div>

    <ul v-else class="lod-list">
      <li v-for="row in offlineRows" :key="row.num" class="lod-row">
        <div class="lod-row-main">
          <div class="lod-code">GSYN-{{ row.num }}</div>
          <div class="lod-name" :title="row.mc">{{ row.mc || '未命名原表' }}</div>
          <div class="lod-meta">
            <span :class="['lod-status', statusClass(row)]">{{ statusText(row) }}</span>
            <span v-if="row.mtime" class="lod-time">{{ formatMtime(row.mtime) }}</span>
          </div>
        </div>
        <div class="lod-actions">
          <button
            type="button"
            class="lod-btn lod-btn-ghost"
            :disabled="!row.hasTpl || !canOperate || row.downloading"
            @click="downloadDocx(row)"
          >
            <svg class="lod-ico" viewBox="0 0 16 16" aria-hidden="true">
              <path fill="currentColor" d="M8 14.5L3.5 10H6V5h4v5h2.5L8 14.5zM2 2h12v2H2V2z"/>
            </svg>
            {{ row.downloading ? '…' : (row.exists ? '重新下载' : '下载') }}
          </button>
          <button
            v-if="row.exists"
            type="button"
            class="lod-btn lod-btn-ghost"
            :disabled="!row.hasTpl || !canOperate || row.previewing"
            @click="previewDocx(row)"
          >{{ row.previewing ? '…' : '预览' }}</button>
          <button
            type="button"
            class="lod-btn lod-btn-solid"
            :disabled="!row.hasTpl || !canOperate || row.uploading"
            @click="triggerUpload(row)"
          >
            <svg class="lod-ico" viewBox="0 0 16 16" aria-hidden="true">
              <path fill="currentColor" d="M8 1.5L12.5 6H10v5H6V6H3.5L8 1.5zM2 12h12v2H2v-2z"/>
            </svg>
            {{ row.uploading ? '…' : '上传' }}
          </button>
        </div>
      </li>
    </ul>

    <input
      ref="docxFileInput"
      type="file"
      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      class="hidden-file-input"
      @change="onDocxFilePicked"
    >

    <el-dialog
      :title="previewTitle"
      :visible.sync="previewVisible"
      width="92%"
      top="3vh"
      append-to-body
      custom-class="lod-preview-dialog"
      @closed="onPreviewClosed"
    >
      <div v-loading="previewLoading" class="lod-preview-body" element-loading-text="正在生成预览图…">
        <div v-if="previewPages.length" class="lod-preview-pages">
          <div
            v-for="pg in previewPages"
            :key="pg.page"
            class="lod-preview-page"
          >
            <div class="lod-preview-page-label">第 {{ pg.page }} 页</div>
            <img :src="pg.url" :alt="'第' + pg.page + '页'" class="lod-preview-img">
          </div>
        </div>
        <div v-else-if="!previewLoading" class="lod-preview-empty">无法加载预览</div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/utils/api'
import { userParams } from '@/utils/user'
import axios from 'axios'

const API_BASE = process.env.VUE_APP_API_BASE || ''

export default {
  name: 'LabOfflineDocxPanel',
  props: {
    xmbh: { type: String, default: '' },
    formNums: { type: Array, default: () => [] },
    labForms: { type: Array, default: () => [] },
    canOperate: { type: Boolean, default: true }
  },
  data() {
    return {
      statusLoading: false,
      docxStatusMap: {},
      rowBusy: {},
      uploadTargetNum: '',
      previewVisible: false,
      previewLoading: false,
      previewPages: [],
      previewTitle: '原表预览'
    }
  },
  computed: {
    offlineRows() {
      const nums = Array.from(new Set((this.formNums || []).map(String).filter(Boolean)))
      return nums.map(num => {
        const hit = (this.labForms || []).find(f => String(f.num) === String(num))
        const st = this.docxStatusMap[num] || {}
        const busy = this.rowBusy[num] || {}
        return {
          num,
          mc: (hit && hit.mc) || '',
          hasTpl: st.has_tpl !== undefined ? !!st.has_tpl : true,
          exists: !!st.exists,
          mtime: st.mtime || 0,
          downloading: !!busy.downloading,
          uploading: !!busy.uploading,
          previewing: !!busy.previewing
        }
      })
    }
  },
  watch: {
    xmbh() {
      this.refreshStatus()
    },
    formNums: {
      deep: true,
      handler() {
        this.refreshStatus()
      }
    }
  },
  mounted() {
    this.refreshStatus()
  },
  methods: {
    statusText(row) {
      if (!row.hasTpl) return '无模板'
      if (row.exists) return '已上传'
      return '待下载'
    },
    statusClass(row) {
      if (!row.hasTpl) return 'is-muted'
      if (row.exists) return 'is-ok'
      return 'is-wait'
    },
    async downloadDocx(row) {
      if (!row || !row.hasTpl || !this.xmbh) return
      this.setRowBusy(row.num, 'downloading', true)
      try {
        const params = userParams({
          yn_tr_lab_docx_download: '1',
          xmbh: this.xmbh,
          form_num: row.num,
          scope: 'batch'
        })
        const res = await axios.get(`${API_BASE}/yn_api.php`, {
          params,
          responseType: 'blob'
        })
        const ct = String((res.headers && res.headers['content-type']) || '')
        if (ct.indexOf('application/json') >= 0 || ct.indexOf('text/') >= 0) {
          let text = ''
          if (res.data && typeof res.data.text === 'function') {
            text = await res.data.text()
          } else if (res.data instanceof Blob) {
            text = await new Promise((resolve, reject) => {
              const fr = new FileReader()
              fr.onload = () => resolve(String(fr.result || ''))
              fr.onerror = reject
              fr.readAsText(res.data)
            })
          }
          let msg = '下载失败'
          try {
            const j = JSON.parse(text)
            if (j && j.error) msg = j.error
          } catch (e) { /* ignore */ }
          this.$message.error(msg)
          return
        }
        const blob = res.data
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `GSYN-${row.num}.docx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        this.$message.success('已开始下载预填表')
        await this.refreshStatus()
      } catch (e) {
        this.$message.error('下载失败')
      } finally {
        this.setRowBusy(row.num, 'downloading', false)
      }
    },
    formatMtime(ts) {
      const n = Number(ts) || 0
      if (!n) return ''
      const d = new Date(n * 1000)
      const p = v => (v < 10 ? '0' + v : '' + v)
      return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    setRowBusy(num, key, val) {
      const cur = { ...(this.rowBusy[num] || {}) }
      cur[key] = val
      this.$set(this.rowBusy, String(num), cur)
    },
    async refreshStatus() {
      const forms = Array.from(new Set((this.formNums || []).map(String).filter(Boolean)))
      if (!this.xmbh || !forms.length) {
        this.docxStatusMap = {}
        return
      }
      this.statusLoading = true
      try {
        const res = await api.get('yn_tr_lab_docx_status', {
          xmbh: this.xmbh,
          forms: JSON.stringify(forms),
          scope: 'batch'
        })
        const d = res.data || {}
        if (d.error) {
          this.$message.error(d.error)
          return
        }
        const map = {}
        ;(d.items || []).forEach(it => {
          map[String(it.form_num)] = {
            has_tpl: Number(it.has_tpl) === 1,
            exists: Number(it.exists) === 1,
            mtime: Number(it.mtime) || 0,
            size: Number(it.size) || 0
          }
        })
        this.docxStatusMap = map
      } catch (e) {
        this.$message.error('加载原表状态失败')
      } finally {
        this.statusLoading = false
      }
    },
    async previewDocx(row) {
      if (!row || !row.exists || !this.xmbh) return
      this.setRowBusy(row.num, 'previewing', true)
      this.previewVisible = true
      this.previewLoading = true
      this.previewPages = []
      this.previewTitle = `GSYN-${row.num} ${row.mc || ''}`.trim()
      try {
        const res = await api.get('yn_tr_lab_docx_preview', {
          xmbh: this.xmbh,
          form_num: row.num,
          scope: 'batch'
        })
        const d = res.data || {}
        if (d.error) {
          this.$message.error(d.error)
          this.previewVisible = false
          return
        }
        const pages = (d.pages || []).filter(p => p && p.url)
        if (!pages.length) {
          this.$message.error('未生成预览图')
          this.previewVisible = false
          return
        }
        this.previewPages = pages
      } catch (e) {
        this.$message.error('预览加载失败')
        this.previewVisible = false
      } finally {
        this.previewLoading = false
        this.setRowBusy(row.num, 'previewing', false)
      }
    },
    onPreviewClosed() {
      this.previewPages = []
    },
    triggerUpload(row) {
      if (!row || !row.hasTpl) return
      this.uploadTargetNum = String(row.num)
      const input = this.$refs.docxFileInput
      if (input) {
        input.value = ''
        input.click()
      }
    },
    async onDocxFilePicked(ev) {
      const file = ev && ev.target && ev.target.files && ev.target.files[0]
      const num = this.uploadTargetNum
      this.uploadTargetNum = ''
      if (!file || !num || !this.xmbh) return
      const name = String(file.name || '').toLowerCase()
      if (!name.endsWith('.docx')) {
        this.$message.warning('请选择 .docx 文件')
        return
      }
      this.setRowBusy(num, 'uploading', true)
      try {
        const res = await api.upload('yn_tr_lab_docx_upload', file, {
          xmbh: this.xmbh,
          form_num: num,
          scope: 'batch'
        })
        const d = res.data || {}
        if (d.error) {
          this.$message.error(d.error)
          return
        }
        this.$message.success(`GSYN-${num} 已上传并入库`)
        this.$emit('uploaded', { form_num: num, ...d })
        await this.refreshStatus()
      } catch (e) {
        this.$message.error('上传失败')
      } finally {
        this.setRowBusy(num, 'uploading', false)
      }
    }
  }
}
</script>

<style scoped>
.lab-offline-docx {
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  overflow: hidden;
}
.lod-hd {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid #eef0f3;
  background: #f8f9fb;
}
.lod-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2329;
  letter-spacing: 0.02em;
}
.lod-hint {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: #8a919f;
}
.lod-refresh {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: #3370ff;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}
.lod-refresh:hover:not(:disabled) {
  background: rgba(51, 112, 255, 0.08);
}
.lod-refresh:disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}
.lod-ico {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}
.lod-empty {
  padding: 36px 16px;
  text-align: center;
  font-size: 13px;
  color: #8a919f;
}
.lod-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: calc(100vh - 360px);
  overflow: auto;
}
.lod-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
}
.lod-row:last-child {
  border-bottom: none;
}
.lod-row:hover {
  background: #fafbfd;
}
.lod-row-main {
  flex: 1;
  min-width: 0;
}
.lod-code {
  font-size: 12px;
  font-weight: 600;
  color: #3370ff;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
}
.lod-name {
  margin-top: 2px;
  font-size: 13px;
  color: #1f2329;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lod-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.lod-status {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 3px;
  font-size: 11px;
  line-height: 18px;
}
.lod-status.is-ok {
  color: #2a9b6a;
  background: #e8f7f0;
}
.lod-status.is-wait {
  color: #b88230;
  background: #fff6e6;
}
.lod-status.is-muted {
  color: #8a919f;
  background: #f2f3f5;
}
.lod-time {
  font-size: 11px;
  color: #a0a6b0;
  font-variant-numeric: tabular-nums;
}
.lod-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.lod-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 56px;
  height: 28px;
  padding: 0 10px;
  border-radius: 5px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.lod-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.lod-btn-ghost {
  background: #fff;
  border-color: #d0d5dd;
  color: #4e5969;
}
.lod-btn-ghost:hover:not(:disabled) {
  border-color: #3370ff;
  color: #3370ff;
  background: #f5f8ff;
}
.lod-btn-solid {
  background: #3370ff;
  border-color: #3370ff;
  color: #fff;
}
.lod-btn-solid:hover:not(:disabled) {
  background: #245bdb;
  border-color: #245bdb;
}
.hidden-file-input {
  display: none;
}
.lod-preview-body {
  height: 78vh;
  min-height: 480px;
  background: #e8eaed;
  overflow: auto;
}
.lod-preview-pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px;
}
.lod-preview-page {
  width: 100%;
  max-width: 920px;
}
.lod-preview-page-label {
  margin-bottom: 6px;
  font-size: 12px;
  color: #8a919f;
}
.lod-preview-img {
  display: block;
  width: 100%;
  max-width: 900px;
  height: auto;
  margin: 0 auto;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  image-rendering: auto;
  -ms-interpolation-mode: bicubic;
}
.lod-preview-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8a919f;
}
</style>

<style>
.lod-preview-dialog .el-dialog__body {
  padding: 8px 12px 12px;
}
</style>
